import React, {useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {VelocityTransitionGroup} from 'velocity-react';
import Animations from '~/constants/Animations';
import Keys from '~/constants/Keyboard';
import {useSelector, useDispatch} from 'react-redux';

import * as NotificationConstants from '~/constants/NotificationConstants';
import useSessionModals from '~/hooks/useSessionModals';

// components
import NotificationSwitcher from '~/components/NotificationSwitcher';
import AddProductMissingAreas from './AddProductMissingAreas';
import ChangePrintMethodWarning from '~/components/PrintMethodCard/WarningModals/ChangePrintMethodWarning';
import DesignLoadErrorNotification from './DesignLoadErrorNotification';
import MultiStyleNamesAndNumbersNotification from './MultiStyleNamesAndNumbersNotification';
import QuoteWithoutDesign from './QuoteWithoutDesign';
import BuyBlankFromStore from './BuyBlankFromStore';
import RetiredProductColorNotification from './RetiredProductColorNotification';
import InactiveProductNotification from './InactiveProductNotification';
import IncompatibleNamesAndNumbersNotification from './IncompatibleNamesAndNumbersNotification';
import ServiceUnavailableNotification from './ServiceUnavailableNotification';
import SizesUnavailableWarning from './SizesUnavailableWarning';
import StyleLoadErrorNotification from './StyleLoadErrorNotification';
import SwapIncompatibleProductsWarning from './SwapIncompatibleProductsWarning';
import SwapProductMismatchedAreas from './SwapProductMismatchedAreas';
import SwapProductsWarning from './SwapProductsWarning';
import TooManyColors from './TooManyColors';
import TooManyProducts from './TooManyProducts';
import TooManySleeveColors from './TooManySleeveColors';
import FundraisingLoadErrorNotification from './FundraisingLoadErrorNotification';
import UploadDimensionsError from './UploadDimensionsError';
import UploadFilesizeError from './UploadFilesizeError';
import UploadFileUnsupportedError from './UploadFileUnsupportedError';
import UnsupportedDesignAreasWarning from '~/components/PrintMethodCard/WarningModals/UnsupportedDesignAreasWarning';
import FontOutlineUnsupported from './FontOutlineUnsupported';
import FontMinHeightError from './FontMinHeightError';
import FontPixelWidthError from './FontPixelWidthError';
import InvalidUploadNotification from './InvalidUploadNotification';
import NamesAndNumbersAndMultiColorError from './NamesAndNumbersAndMultiColorError';
import LaserEngravedModal from '~/components/LaserEngravedModal';
import FailedLoadSleeveProductImage from './FailedLoadSleeveProductImage';
import DeleteSleeveDesignWarning from './DeleteSleeveDesignWarning';
import DebossModal from '~/components/DebossModal';
import EmbossModal from '~/components/EmbossModal';
import MinQtyInternationalShippingNotification from './MinQtyInternationalShippingNotification';
import ColorChangePantoneWarning from './ColorChangePantoneWarning';
import RemovePantoneWarning from './RemovePantoneWarning';

// Redux Selectors
import {getCurrentSelection} from '~/selectors/ProductSelectors';
import {getNotification} from '~/selectors/NotificationSelectors';

// Redux Actions
import {dismiss} from '~/actions/NotificationActions';
import {swapProduct} from '~/actions/ProductActions';
import PantoneDisabledWarning from './PantoneDisabledWarning';
import PersonalizationNotSupportedWarning from './PersonalizationNotSupportedWarning';

import './index.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const NotificationContainer = ({onHide = () => {}, onShow = () => {}}) => {
  const dispatch = useDispatch();
  const notification = useSelector(getNotification);

  const shouldRender = useSessionModals(notification);

  const currentStyle = useSelector(getCurrentSelection);

  const closeModal = useCallback(() => {
    dispatch(dismiss());
  }, [dispatch]);

  // on Mount
  useEffect(() => {
    const handleKeyDown = evt => {
      if (evt.key === Keys.ESCAPE) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [dispatch, closeModal]);

  useEffect(() => {
    if (notification?.key && shouldRender) {
      onShow();
    }
    return () => {
      onHide();
    };
  }, [notification?.key, onShow, onHide, shouldRender]);

  const proceedToSwapProduct = selectedProductData => {
    return dispatch(
      swapProduct({
        previousColorId: currentStyle.colorId,
        previousStyleId: currentStyle.styleId,
        nextProduct: selectedProductData,
        isAD: false,
        clearMismatchedViews: false,
        clearSizeDropWarning: true,
        clearPreviousProducts: true,
        isSwappingEmbroidToPrintVariant: false,
        isInitialLabLoad: false,
        fireAnalytics: true,
        rearrangeIcons: true,
        useSpecialGuidelineOnIconRearranger: true,
        preventHighInkColorAlert: false,
        shouldColorMatch: false
      })
    );
  };

  const classes = classnames('ndx-NotificationContainer', {
    'ndx-NotificationContainer--active': notification
  });

  const switcherProps = notification && {
    currentKey: notification.key,
    description: notification.description,
    dismiss: closeModal,
    notification: notification,
    title: notification.title,
    type: notification.type,
    undismissable: notification.dismissable === false
  };

  return (
    shouldRender && (
      <div className={classes} onMouseDown={closeModal}>
        <VelocityTransitionGroup
          enter={Animations.blinkIn}
          leave={Animations.blinkOut}>
          {notification && (
            <NotificationSwitcher {...switcherProps}>
              <AddProductMissingAreas
                name={NotificationConstants.ADD_PRODUCT_MISSING_AREAS}
              />
              <BuyBlankFromStore
                name={NotificationConstants.BUY_BLANK_FROM_STORE}
              />
              <ChangePrintMethodWarning
                name={NotificationConstants.CHANGE_PRINT_METHOD_WARNING}
              />
              <ColorChangePantoneWarning
                name={NotificationConstants.COLOR_CHANGE_PANTONE_WARNING}
              />
              <DebossModal name={NotificationConstants.DEBOSS_DESIGN_TIPS} />
              <DeleteSleeveDesignWarning
                name={NotificationConstants.DELETE_SLEEVE_DESIGN_WARNING}
              />
              <DesignLoadErrorNotification
                name={NotificationConstants.DESIGN_LOAD_ERROR}
              />
              <EmbossModal name={NotificationConstants.EMBOSS_DESIGN_TIPS} />
              <FailedLoadSleeveProductImage
                name={NotificationConstants.FAILED_LOAD_SLEEVE_PRODUCT_IMAGE}
              />
              <FontMinHeightError
                name={NotificationConstants.FONT_MIN_HEIGHT_ERROR}
              />
              <FontOutlineUnsupported
                name={NotificationConstants.FONT_OUTLINE_UNSUPPORTED}
              />
              <FontPixelWidthError
                name={NotificationConstants.FONT_PIXEL_WIDTH_ERROR}
              />
              <FundraisingLoadErrorNotification
                name={NotificationConstants.FUNDRAISING_LOAD_ERROR}
              />
              <InactiveProductNotification
                name={NotificationConstants.INACTIVE_PRODUCT}
              />
              <IncompatibleNamesAndNumbersNotification
                name={NotificationConstants.INCOMPATIBLE_NAMES_AND_NUMBERS}
              />
              <InvalidUploadNotification
                name={NotificationConstants.INVALID_UPLOAD}
              />
              <LaserEngravedModal
                name={NotificationConstants.LASER_ENGRAVING_DESIGN_TIPS}
              />
              <MinQtyInternationalShippingNotification
                name={NotificationConstants.MIN_QTY_INTERNATIONAL_SHIPPING}
              />
              <MultiStyleNamesAndNumbersNotification
                name={NotificationConstants.MULTI_STYLE_NAMES_AND_NUMBERS}
              />
              <NamesAndNumbersAndMultiColorError
                name={NotificationConstants.NAMES_NUMBERS_MULTI_COLOR}
              />
              <PantoneDisabledWarning
                name={NotificationConstants.PANTONE_DISABLED_WARNING}
              />
              <PersonalizationNotSupportedWarning
                name={
                  NotificationConstants.PERSONALIZATION_NOT_SUPPORTED_WARNING
                }
              />
              <QuoteWithoutDesign
                name={NotificationConstants.QUOTE_WITHOUT_DESIGN}
              />
              <RemovePantoneWarning
                name={NotificationConstants.REMOVE_PANTONE_WARNING}
              />
              <RetiredProductColorNotification
                name={NotificationConstants.RETIRED_PRODUCT_COLOR}
              />
              <ServiceUnavailableNotification
                name={NotificationConstants.SERVICE_UNAVAILABLE}
              />
              <SizesUnavailableWarning
                name={NotificationConstants.SIZES_UNAVAILABLE_WARNING}
              />
              <StyleLoadErrorNotification
                name={NotificationConstants.STYLE_LOAD_ERROR}
              />
              <SwapProductMismatchedAreas
                name={NotificationConstants.SWAP_PRODUCT_MISMATCHED_AREAS}
              />
              <SwapProductsWarning
                name={NotificationConstants.SWAP_PRODUCTS_WARNING}
              />
              <SwapIncompatibleProductsWarning
                name={NotificationConstants.SWAP_INCOMPATIBLE_PRODUCTS_WARNING}
                swapProduct={proceedToSwapProduct}
              />
              <TooManyColors name={NotificationConstants.TOO_MANY_COLORS} />
              <TooManyProducts name={NotificationConstants.TOO_MANY_PRODUCTS} />
              <TooManySleeveColors
                name={NotificationConstants.TOO_MANY_SLEEVE_COLORS}
              />
              <UnsupportedDesignAreasWarning
                name={NotificationConstants.UNSUPPORTED_DESIGN_AREAS_WARNING}
              />
              <UploadDimensionsError
                name={NotificationConstants.UPLOAD_DIMENSIONS_TOO_LARGE}
              />
              <UploadFilesizeError
                name={NotificationConstants.UPLOAD_FILESIZE_TOO_LARGE}
              />
              <UploadFileUnsupportedError
                name={NotificationConstants.UPLOAD_FILE_UNSUPPORTED}
              />
            </NotificationSwitcher>
          )}
        </VelocityTransitionGroup>
      </div>
    )
  );
};

NotificationContainer.displayName = 'NotificationContainer';
const propTypes = {
  cartPrimaryAction: PropTypes.func,
  notification: PropTypes.oneOfType([
    PropTypes.shape({
      description: PropTypes.string,
      dismissable: PropTypes.bool,
      errorText: PropTypes.string,
      key: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string
    })
  ]),
  onHide: PropTypes.func,
  onShow: PropTypes.func
};

NotificationContainer.propTypes = propTypes;

export default NotificationContainer;
